import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import BreadCrumb from "src/Components/GlobalComponents/BreadCrumb";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import SearchFilterInput from "src/Components/GlobalComponents/SearchFilterInput";
import SelectDropdown from "src/Components/GlobalComponents/SelectDropdown";
import TableWrapper from "src/Components/GlobalComponents/TableWrapper";
import SimpleInput from "src/Components/SimpleInput";
import httpRequest from "src/helpers/httpRequest";
import { IDropDownArray, IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import qs from "qs";
import moment from "moment";
import { CURRENCY, statusColor } from "src/lib/constans";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { canWrite } from "src/helpers/helperFunctions";
import CustomWebsiteDropdown from "src/Components/GlobalComponents/Widget/CustomWebsiteDropdown";

interface IItem {
  title: string;
  isOnlineAppoinment: boolean;
  [key: string]: any;
} // Interface for dropdown items (status and appointment type)

interface iFilterItems {
  status: string;
  search: string;
  startDate: string;
  endDate: string;
}

interface IAppointmentItems {
  _id: string;
  customerFirstName: string;
  customerLastName: string;
  doctorFirstName: string;
  doctorLastName: string;
  startTime: string;
  endTime: string;
  appoinmentDate: string;
  status: string;
  totalAmount: number;
  [key: string]: any;
}

interface IAppointmentResponse {
  documents: IAppointmentItems[];
  paginated: IPaginated;
} // Interface for the API response containing appointments and pagination info

const AllApointments: React.FC = () => {
  const { allIndicators } = useSelector((store: RootState) => store?.redDots);
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(
    userDetails,
    "userAppointmentManagement",
    false
  );

  const statusList: IDropDownArray[] = [
    { label: "Pending", value: "pending" },
    { label: "Complete", value: "completed" },
    { label: "Cancelled", value: "cancelled" },
  ]; // Status filter options

  const tabList: IItem[] = [
    {
      title: "Online Appointment",
      isOnlineAppoinment: true,
      count: allIndicators?.adminOnlineAppointment,
    },
    {
      title: "Physical Appointment",
      isOnlineAppoinment: false,
      count: allIndicators?.adminPhysicalAppointment,
    },
  ]; // Appointment type tabs (Online/Physical)

  // States for loading status, pagination, and customer data
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination

  const [allAppointments, setAllAppointments] = useState<IAppointmentItems[]>(
    []
  );
  const [inputKey, setInputKey] = useState(0); // Key to force re-render

  const [filterItems, setFilterItems] = useState<iFilterItems>({
    status: "",
    search: "",
    startDate: "",
    endDate: "",
  });

  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    isOnlineAppoinment: true, // Filter by appointment type (online or physical)
    status: "",
    search: "",
    startDate: "",
    endDate: "",
  });

  const resetFilter = () => {
    setInputKey((prev) => prev + 1);
    setFilterItems({
      status: "",
      search: "",
      startDate: "",
      endDate: "",
    });

    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      status: "",
      search: "",
      startDate: "",
      endDate: "",
    }));
  };

  const applyFilter = () => {
    const { status, startDate, endDate, search } = filterItems;
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      status,
      search,
      startDate,
      endDate,
    }));
  };

  // Handles tab click to filter orders
  const handleTabClick = (isOnlineAppoinment: boolean) => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      isOnlineAppoinment,
    }));
  };

  // Handles page change in pagination
  const handlePageChange = (e: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  // API_CALL: Function to fetch the list of all appointments from the API
  const fetchAllAppointments = async () => {
    setIsLoader(true);
    const { res, err } = await httpRequest<IAppointmentResponse>({
      method: "post",
      path: "/appoiment",
      params: queryParams,
    });
    if (res) {
      const { documents = [], paginated } = res;
      setAllAppointments(documents);
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" }); // Show error toast if API call fails
    }
    setIsLoader(false);
  };

  useEffect(() => {
    fetchAllAppointments();
  }, [queryParams]); // Fetch appointments when queryParams change

  const handleDateChange = (key: keyof iFilterItems, val: string) => {
    const currentStartDate = filterItems.startDate
      ? new Date(filterItems.startDate)
      : null;
    const currentEndDate = filterItems.endDate
      ? new Date(filterItems.endDate)
      : null;
    const newDate = val ? new Date(val) : null;

    if (newDate) {
      if (key === "startDate" && currentEndDate && newDate > currentEndDate) {
        toast("Start Date cannot be later than End Date.", { type: "error" });
        return; // Stop further execution
      }
      if (key === "endDate" && currentStartDate && newDate < currentStartDate) {
        toast("End Date cannot be earlier than Start Date.", { type: "error" });
        return; // Stop further execution
      }
    }

    setFilterItems((prev) => ({ ...prev, [key]: val }));
  };

  return (
    <>
      <BreadCrumb
        current="Appointment"
        previous="dashboard"
        navigateUrl="/admin"
        btnShow={false}
      />
      <div className="table-container">
        <div className="products-tabs-btn md:col-span-2 col-span-1">
          {tabList.map((v: IItem, index: number) => (
            <button
              key={index}
              className={`relative font-size-15px font-Poppins-Medium ${
                queryParams.isOnlineAppoinment === v.isOnlineAppoinment &&
                "active"
              }`}
              onClick={() => handleTabClick(v.isOnlineAppoinment)}
            >
              {v.title}
              {v.count > 0 && (
                <div className="red-dot-show absolute -top-2 -right-1"></div>
              )}
            </button>
          ))}
        </div>
        <hr className="products-tabs-hr-allOrder" />
        <div className="flex items-center gap-3 justify-between">
          <div className="flex items-center gap-[0.2rem] lg:gap-[0.3vw]">
            <CustomWebsiteDropdown
              options={statusList}
              setData={(arr) =>
                setFilterItems((prev) => ({
                  ...prev,
                  status: arr[0] ? arr[0].value : "",
                }))
              }
              data={statusList.filter((v) => v.value === filterItems.status)}
              placeholder="Filter by Status"
              dropdownMainId="status-dropdown"
              withoutMargin
              dropdownMainClass="appointment-filter-height"
              mainDivClass="!w-fit appointment-filter-field-container"
            />
            <SimpleInput
              value={filterItems.startDate}
              onChange={(val) => handleDateChange("startDate", val)}
              type="date"
              placeholder="Start Date"
              withoutMargin
              extraClasses="appointment-filter-height"
              inputContainerClasses="appointment-filter-field-container"
            />
            <SimpleInput
              value={filterItems.endDate}
              onChange={(val) => handleDateChange("endDate", val)}
              type="date"
              placeholder="End Date"
              withoutMargin
              extraClasses="appointment-filter-height"
              inputContainerClasses="appointment-filter-field-container"
            />
            <SearchFilterInput
              key={inputKey} // Apply the key here
              onChangeText={(txt: string) =>
                setFilterItems((prev) => ({ ...prev, search: txt }))
              }
              containerExtraStyle={{ display: "block" }}
              extraClasses="appointment-filter-height"
            />
          </div>
          <div className="flex items-center gap-3 justify-end">
            <PrimaryBtn
              disabled={
                !filterItems.status &&
                !filterItems.startDate &&
                !filterItems.endDate &&
                !filterItems.search
              }
              ButtonText="Reset"
              onPress={resetFilter}
              btnExtraStyle={{
                borderRadius: "1.5rem",
                backgroundColor: "#004540",
                padding: "6px 30px",
              }}
            />
            <PrimaryBtn
              ButtonText="Apply"
              onPress={applyFilter}
              btnExtraStyle={{
                borderRadius: "1.5rem",
                backgroundColor: "#004540",
                padding: "6px 30px",
              }}
            />
          </div>
        </div>
        <hr className="products-tabs-hr-allOrder" />
        <TableWrapper
          isLoading={isLoader}
          page={paginated?.currentPage || 1}
          total={paginated?.totalPages || 0}
          thead={[
            "S.No",
            "Doctor Name",
            "Customer Name",
            "Fee",
            "Time",
            "Status",
            "Date",
            "Action",
          ]}
          onChange={handlePageChange}
          columnWidth={"6% 19% 15% 12% 14% 12% 12% 10%"}
        >
          <div className="tbody-content-height-appointments-table">
            {allAppointments?.map((item: IAppointmentItems, index: number) => (
              <ul
                key={index}
                className={`font-size-16px tbody-light-table font-Poppins-Regular grid ${
                  !item?.adminNotified ? "font-bold" : ""
                }`}
                style={{
                  gridTemplateColumns: "6% 19% 15% 12% 14% 12% 12% 10%",
                }}
              >
                <li>
                  {(paginated?.currentPage - 1) * queryParams.limit +
                    (index + 1)}
                </li>
                <li>{`${item.doctorFirstName} ${item.doctorLastName}`}</li>
                <li>{`${item.customerFirstName} ${item.customerLastName}`}</li>
                <li>
                  {CURRENCY} {item.totalAmount}
                </li>
                <li>{`${moment(item.startTime, "HH:mm").format(
                  "h:mm A"
                )} to ${moment(item.endTime, "HH:mm").format("h:mm A")}`}</li>
                <li style={{ paddingLeft: 0, justifyContent: "center" }}>
                  <span
                    className="py-1 px-4 text-white capitalize rounded"
                    style={{
                      backgroundColor:
                        item.status === "pending"
                          ? statusColor.yellow
                          : item.status === "completed"
                          ? statusColor.green
                          : item.status === "cancelled"
                          ? statusColor.red
                          : "",
                    }}
                  >
                    {item.status}
                  </span>
                </li>
                <li>{moment(item.appoinmentDate).format("DD-MM-YYYY")}</li>
                <li className="action-last-colum justify-center">
                  <Link
                    to={
                      !hasWriteAccess
                        ? "#"
                        : `/single-appointment/${item._id}/view`
                    }
                  >
                    <FontAwesomeIcon
                      icon={faEye}
                      className={`${!hasWriteAccess ? "read_access" : ""}`}
                    />
                  </Link>
                </li>
              </ul>
            ))}
          </div>
        </TableWrapper>
      </div>
    </>
  );
};

export default AllApointments;
